import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setHistory, setSize } from 'store/entities/chatbot/actions';
import { selectHistory } from 'store/entities/chatbot/selectors';
import type { ChatHistory, Message } from 'types';

interface LegacyChatHistory {
  id: string;
  messages: Message[];
  mode: string;
  timestamp: number;
}

function isLegacyChatHistory(item: unknown): item is LegacyChatHistory {
  if (typeof item !== 'object' || item === null) return false;
  const obj: object = item;
  return 'mode' in obj && !('assistantId' in obj);
}

function migrateLegacyHistoryItems(items: unknown[]): ChatHistory[] {
  return items
    .map((item): ChatHistory | null => {
      if (isLegacyChatHistory(item)) {
        return { id: item.id, messages: item.messages, assistantId: null, timestamp: item.timestamp };
      }

      if (typeof item === 'object' && item !== null && 'id' in item && 'messages' in item && 'assistantId' in item && 'timestamp' in item) {
        return item as ChatHistory;
      }

      return null;
    })
    .filter((item): item is ChatHistory => item !== null);
}

export const useInitiateByOldData = () => {
  const dispatch = useDispatch();
  const history = useSelector(selectHistory());
  const isChecked = useRef(false);

  useEffect(() => {
    if (isChecked.current) return;
    isChecked.current = true;

    try {
      if (history && history.length > 0) {
        const historyItems: unknown[] = history;
        const needsMigration = historyItems.some(isLegacyChatHistory);
        if (needsMigration) {
          dispatch(setHistory(migrateLegacyHistoryItems(historyItems)));
        }
        localStorage.removeItem('chatbox:size');
        localStorage.removeItem('chatbox:history');
        return;
      }

      const oldSizeStr = localStorage.getItem('chatbox:size');
      const oldHistoryStr = localStorage.getItem('chatbox:history');

      if (oldSizeStr || oldHistoryStr) {
        if (oldSizeStr) dispatch(setSize(JSON.parse(oldSizeStr)));
        if (oldHistoryStr) {
          const parsed: unknown = JSON.parse(oldHistoryStr);
          dispatch(setHistory(migrateLegacyHistoryItems(Array.isArray(parsed) ? parsed : [])));
        }

        localStorage.removeItem('chatbox:size');
        localStorage.removeItem('chatbox:history');
        localStorage.removeItem('chatbox:open');
      }
    } catch (_err) {
      localStorage.removeItem('chatbox:size');
      localStorage.removeItem('chatbox:history');
      localStorage.removeItem('chatbox:open');
    }
  }, [dispatch, history]);
};
